<template>
  <b-overlay
    :show="loading"
    spinner-variant="primary"
    spinner-type="grow"
    spinner-small
    rounded="sm"
  >
    <div>
      <b-card title="Informations de la commande">
        <validation-observer ref="simpleRules">
          <b-form @submit.prevent>
            <b-row>
              <!-- Existing fields -->
              <b-col md="6" xl="4" class="mb-1">
                <b-form-group label="Date" label-for="date">
                  <validation-provider #default="{ errors }" name="Date" rules="required">
                    <b-form-input
                      id="date"
                      v-model="form.date"
                      type="date"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6" xl="4" class="mb-1">
                <b-form-group label="Date d'échéance" label-for="due_date">
                  <b-form-input
                    id="due_date"
                    v-model="form.due_date"
                    type="date"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6" xl="4" class="mb-1">
                <b-form-group label="Status" label-for="status">
                  <validation-provider #default="{ errors }" name="Status" rules="required">
                    <v-select
                      id="status"
                      v-model="form.status"
                      :options="statusOptions"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6" xl="4" class="mb-1">
                <b-form-group label="Type de client" label-for="Type de client">
                  <validation-provider #default="{ errors }" name="Type de client" rules="required">
                    <v-select
                      id="Type de client"
                      v-model="form.client_type"
                      :options="clientTypeOptions"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6" xl="4" class="mb-1">
                <b-form-group label="Numéro de suivi" label-for="tracking">
                  <b-form-input
                    id="tracking"
                    v-model="form.tracking_number"
                    placeholder="Numéro de suivi"
                  />
                </b-form-group>
              </b-col>
              <!-- New fields -->
              <b-col md="6" xl="4" class="mb-1">
                <b-form-group label="Taux de TVA global (%)" label-for="vat_rate">
                  <b-form-input
                    id="vat_rate"
                    v-model="form.vat_rate"
                    type="number"
                    min="0"
                    max="100"
                    @input="calculateTotal"
                  />
                </b-form-group>
              </b-col>
             
              <b-col md="6" xl="4" class="mb-1">
                <b-form-group label="Note TVA" label-for="vat_note">
                  <b-form-input
                    id="vat_note"
                    v-model="form.vat_note"
                    placeholder="Note concernant la TVA (optionnel)"
                  />
                </b-form-group>
              </b-col>

              <!-- Méthode de paiement -->
              <b-col md="6" xl="4" class="mb-1">
                <b-form-group label="Méthode de paiement" label-for="payment_method">
                  <v-select
                    id="payment_method"
                    v-model="form.payment_method"
                    :options="paymentMethodOptions"
                    :reduce="option => option.value"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6" xl="4" class="mb-1">
                <b-form-group label="Status de facture" label-for="invoice_status">
                  <validation-provider #default="{ errors }" name="Status de facture" rules="required">
                    <v-select
                      id="invoice_status"
                      v-model="form.invoice_status"
                      :options="invoiceStatusOptions"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card>

      <b-card title="Informations client" class="mt-2">
        <b-row>
          <b-col md="6" xl="4" class="mb-1">
            <b-form-group label="Client" label-for="user">
              <validation-provider #default="{ errors }" name="Client">
                <v-select
                  v-model="form.user"
                  :options="userOptions"
                  label="title"
                  :reduce="option => ({ id: option.value })"
                  :state="errors.length > 0 ? false : null"
                  @input="loadUserDetails"
                  :clearable="true"
                  placeholder="Sélectionnez un client ou saisissez un nom"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          
          <!-- Champ nom client personnalisé -->
          <b-col md="6" xl="4" class="mb-1" v-if="!form.user">
            <b-form-group label="Nom du client" label-for="custom_client_name">
              <b-form-input
                id="custom_client_name"
                v-model="form.custom_client_name"
                placeholder="Nom du client"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Toujours afficher les champs d'adresse -->
        <b-row>
          <b-col md="6" xl="4" class="mb-1">
            <b-form-group label="Téléphone" label-for="phone">
              <b-form-input
                id="phone"
                v-model="form.address.phone"
                placeholder="Téléphone"
              />
            </b-form-group>
          </b-col>
          <b-col md="6" xl="4" class="mb-1">
            <b-form-group label="Adresse 1" label-for="address1">
              <b-form-input
                id="address1"
                v-model="form.address.address1"
                placeholder="Adresse ligne 1"
              />
            </b-form-group>
          </b-col>
          <b-col md="6" xl="4" class="mb-1">
            <b-form-group label="Adresse 2" label-for="address2">
              <b-form-input
                id="address2"
                v-model="form.address.address2"
                placeholder="Adresse ligne 2"
              />
            </b-form-group>
          </b-col>
          <b-col md="6" xl="4" class="mb-1">
            <b-form-group label="Code postal" label-for="postcode">
              <b-form-input
                id="postcode"
                v-model="form.address.postcode"
                placeholder="Code postal"
              />
            </b-form-group>
          </b-col>
          <b-col md="6" xl="4" class="mb-1">
            <b-form-group label="Ville" label-for="city">
              <b-form-input
                id="city"
                v-model="form.address.city"
                placeholder="Ville"
              />
            </b-form-group>
          </b-col>
          <b-col md="6" xl="4" class="mb-1">
            <b-form-group label="Pays" label-for="country">
              <b-form-input
                id="country"
                v-model="form.address.country"
                placeholder="Pays"
                @change="calculateVAT()"
              />
            </b-form-group>
          </b-col>
          <b-col md="6" xl="4" class="mb-1">
                <b-form-group label="Numéro SIRET" label-for="siret_number">
                  <b-form-input
                    id="siret_number"
                    v-model="form.siret_number"
                    placeholder="SIRET (optionnel)"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6" xl="4" class="mb-1">
                <b-form-group label="Numéro TVA" label-for="vat_number">
                  <b-form-input
                    id="vat_number"
                    v-model="form.vat_number"
                    placeholder="Numéro de TVA (optionnel)"
                  />
                </b-form-group>
              </b-col>
        </b-row>
      </b-card>

      <b-card title="Produits" class="mt-2">
        <b-row v-for="(item, index) in form.order_items" :key="index" class="mb-2">
          <b-col md="2" class="mb-1">
            <b-form-group label="Produit" :label-for="`product-${index}`">
              <v-select
                :id="`product-${index}`"
                v-model="item.glass"
                :options="glassOptions"
                label="title"
                :reduce="option => ({ id: option.value })"
                @input="updateItemPrice(index)"
              />
            </b-form-group>

            <b-form-group label="Nom du produit" :label-for="`product_name-${index}`">
              <b-form-input
                :id="`product_name-${index}`"
                v-model="item.product_name"
                type="text"
                
                
              />
            </b-form-group>

            <b-form-group label="Description produit" :label-for="`product_description-${index}`">
              <b-form-textarea
                :id="`product_description-${index}`"
                v-model="item.product_description"
                rows="3"
              />
            </b-form-group>

          </b-col>
          <b-col md="1" class="mb-1">
            <b-form-group label="Quantité" :label-for="`quantity-${index}`">
              <b-form-input
                :id="`quantity-${index}`"
                v-model="item.quantity"
                type="number"
                min="1"
                @input="calculateTotal"
              />
            </b-form-group>
          </b-col>
          <b-col md="2" class="mb-1">
            <b-form-group label="Prix unitaire HT" :label-for="`price-${index}`">
              <b-form-input
                :id="`price-${index}`"
                v-model="item.price"
                type="number"
                step="0.01"
                @input="calculateTotal"
              />
            </b-form-group>
          </b-col>
          <b-col md="1" class="mb-1">
            TVA : {{ form.vat_rate }}%
          
          </b-col>
          <b-col md="2" class="mb-1">
            <b-form-group label="Total HT" :label-for="`total-ht-${index}`">
              <b-form-input
                :id="`total-ht-${index}`"
                :value="(item.price * item.quantity).toFixed(2) + '€'"
                readonly
              />
            </b-form-group>
          </b-col>
          <b-col md="2" class="mb-1">
            <b-form-group label="Total TTC" :label-for="`total-ttc-${index}`">
              <b-form-input
                :id="`total-ttc-${index}`"
                :value="(item.price * item.quantity * (1 + form.vat_rate/100)).toFixed(2) + '€'"
                readonly
              />
            </b-form-group>
          </b-col>
          <b-col md="2" class="d-flex align-items-end mb-1">
            <b-button 
              variant="outline-danger" 
              size="sm" 
              @click="removeItem(index)"
              class="mb-1"
            >
              <feather-icon icon="TrashIcon" />
            </b-button>
          </b-col>
        </b-row>
        
        <b-row>
          <b-col>
            <b-button 
              variant="outline-primary" 
              size="sm" 
              @click="addItem"
            >
              + Ajouter un produit
            </b-button>
          </b-col>
        </b-row>

        <b-row class="mt-3">
          <b-col md="6" offset-md="6">
            <b-list-group>
              <!-- Tax-exclusive subtotal -->
              <b-list-group-item class="d-flex justify-content-between">
                <span>Sous-total HT :</span>
                <span>{{ subtotalNoVAT.toFixed(2) }}€</span>
              </b-list-group-item>
              
              <!-- VAT amount -->
            
              
              <!-- Tax-inclusive subtotal -->
             
              <b-list-group-item class="d-flex justify-content-between">
                <span>Promotion (%) :</span>
                <b-form-input 
                  v-model.number="promotionAmount" 
                  type="number" 
                  min="0"
                  max="100"
                  size="sm"
                  @input="calculateTotal"
                />
              </b-list-group-item>
              <!-- Delivery -->
              <b-list-group-item class="d-flex justify-content-between">
                <span>Livraison :</span>
                <b-form-input 
                  v-model.number="form.delivery_amount" 
                  type="number" 
                  step="0.01"
                  size="sm"
                  @input="calculateTotal"
                />
              </b-list-group-item>

              <b-list-group-item class="d-flex justify-content-between">
                <span>Total HT :</span>
                <span>{{ (subtotal*1+form.delivery_amount*1).toFixed(2) }}€</span>
              </b-list-group-item>
              
              <!-- Promotion -->
              <b-list-group-item class="d-flex justify-content-between">
                <span>TVA :</span>
                <span>{{ totalVAT.toFixed(2) }}€</span>
              </b-list-group-item>
              
              <!-- Final total -->
              <b-list-group-item class="d-flex justify-content-between font-weight-bold">
                <span>Total TTC :</span>
                <span>{{ total.toFixed(2) }}€</span>
              </b-list-group-item>
            </b-list-group>
          </b-col>
        </b-row>
      </b-card>

      <b-card title="Note" class="mt-2">
        <b-form-group label="Note" label-for="note">
          <b-form-textarea
            id="note"
            v-model="form.note"
            placeholder="Notes concernant la commande"
            rows="3"
          />
        </b-form-group>
      </b-card>

      <div class="mt-2 d-flex justify-content-between">
        <b-button
          variant="primary"
          @click="createOrder"
        >
          Modifier la commande
        </b-button>
        
        <!-- <b-button
          variant="outline-secondary"
          @click="generatePDF"
        >
          Télécharger facture (PDF)
        </b-button> -->
      </div>
    </div>
  </b-overlay>
</template>

<script>
import {
  BButton,
  BFormInput,
  BOverlay,
  BForm,
  BRow,
  BCol,
  BFormGroup,
  BCard,
  BListGroup,
  BListGroupItem,
  BFormTextarea
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import vSelect from 'vue-select'
import { APIRequest, Auth } from '@simb2s/senseye-sdk'
import Ripple from 'vue-ripple-directive'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
export default {
  name: 'CreateOrder',
  components: {
    BButton,
    BFormInput,
    BFormGroup,
    BRow,
    BCol,
    BCard,
    BOverlay,
    BFormTextarea,
    vSelect,
    ValidationProvider,
    ValidationObserver,
    BForm,
    BListGroup,
    BListGroupItem
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      loading: true,
      order:null,
      invoiceStatusOptions: [
        { label: 'En attente de facturation', value: 'pending' },
        { label: 'Envoyée', value: 'invoiced' },
        { label: 'Payée', value: 'paid' }
        
      ],
      loaded:false,
      userOptions: [],
      glassOptions: [],
      statusOptions: [
        { label: 'En attente', value: 'pending' },
        { label: 'En production', value: 'printing' },
        { label: 'Envoyée', value: 'dispatched' },
        { label: 'Scans effectués', value: 'scanned' },
        { label: 'Livrée', value: 'delivered' },
        { label: 'Complétée', value: 'completed' },
        { label: 'Échouée', value: 'failed' },
        { label: 'Annulée', value: 'canceled' },
      ],
      promotionAmount: 0,
      subtotalNoVAT: 0,
      subtotal: 0,
      totalVAT: 0,
      addresses: [],
      total: 0,
      clientTypeOptions: [
        { label: 'B2B', value: 'b2b' },
        { label: 'B2C', value: 'b2c' },
   
      ],
      paymentMethodOptions: [
        { label: 'Carte bancaire', value: 'cb' },
        { label: 'PayPal', value: 'paypal' },
        { label: 'Stripe', value: 'stripe' },
        { label: 'Espèces', value: 'espece' },
        { label: 'Chèque', value: 'cheque' },
        { label: 'Virement', value: 'virement' }
      ],
      form: {
        date: null,
        due_date: null,
        vat_rate: 20, // Global VAT rate for the order
        vat_note: '',
        siret_number: '',
        vat_number: '',
        invoice_status: { label: 'En attente', value: 'pending' },
        client_type:'b2c',
        platform:'backoffice',
        custom_client_name: '',
        status: { label: 'En attente', value: 'pending' },
        tracking_number: '',
        user: null,
        address: {
          phone: '',
          address1: '',
          address2: '',
          city: '',
          postcode: '',
          country: ''
        },
        order_items: [
          {
            glass: null,
            quantity: 1,
            price: 0,
            product_name:"",
            product_description:""
          }
        ],
        delivery_amount: 0,
        promotion: null,
        amount: 0,
        note: '',
        payment_method: 'cb', // Valeur par défaut
      }
    }
  },
  methods: {
    getCountryStatus(countryName) {
    // Normaliser le nom du pays (minuscules, sans accents)
    const normalizedCountry = this.normalizeString(countryName);
    
    // Vérifier si c'est la France
    if (['france', 'fr', 'la france'].includes(normalizedCountry)) {
      return 'FRANCE';
    }
    
    // Liste des pays de l'UE (à jour avril 2025)
    const euCountries = [
      'allemagne', 'autriche', 'belgique', 'bulgarie', 'chypre', 'croatie',
      'danemark', 'espagne', 'estonie', 'finlande', 'grece', 'hongrie',
      'irlande', 'italie', 'lettonie', 'lituanie', 'luxembourg', 'malte',
      'pays-bas', 'pologne', 'portugal', 'republique tcheque', 'roumanie',
      'slovaquie', 'slovenie', 'suede', 'germany', 'austria', 'belgium', 
      'bulgaria', 'cyprus', 'croatia', 'denmark', 'spain', 'estonia', 
      'finland', 'greece', 'hungary', 'ireland', 'italy', 'latvia', 
      'lithuania', 'luxembourg', 'malta', 'netherlands', 'poland', 
      'portugal', 'czech republic', 'romania', 'slovakia', 'slovenia', 'sweden'
    ];
    
    // Vérifier si c'est un pays de l'UE
    if (euCountries.includes(normalizedCountry)) {
      return 'EU';
    }
    
    // Si ce n'est ni la France ni l'UE
    return 'NON_EU';
  },
  
  normalizeString(str) {
    if (!str) return '';
    
    // Convertir en minuscules
    let result = str.toLowerCase();
    
    // Supprimer les accents
    result = result.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    
    return result.trim();
  },
  
  // Ajouter cette méthode pour calculer la TVA en fonction du pays
  calculateVAT() {
    const countryStatus = this.getCountryStatus(this.form.address.country);
    
    // Règles TVA (à adapter selon vos besoins fiscaux)
    if (countryStatus === 'FRANCE') {
      // Appliquer la TVA française standard
      this.form.vat_rate = 20;
      this.form.vat_note = '';
    } else if (countryStatus === 'EU') {
      // Pour les entreprises de l'UE avec numéro de TVA: 0% (autoliquidation)
      this.form.vat_rate = 0;
      this.form.vat_note = '';

    } else { // NON_EU
      // Hors UE: Généralement pas de TVA
      this.form.vat_rate = 0;
      this.form.vat_note = 'Exonération TVA selon article 262-I du CGI';
    }
    
    // Recalculer les totaux avec le nouveau taux de TVA
    this.calculateTotal();
  },
    addItem() {
      this.form.order_items.push({
        glass: null,
        quantity: 1,
        price: 0,
        product_name:"",
        product_description:""
      })
    },
   async removeItem(index) {
      if (this.form.order_items.length > 1) {
        await APIRequest.delete('order-items', this.form.order_items[index].id)
        this.form.order_items.splice(index, 1)
        this.calculateTotal()
      } else {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Une commande doit avoir au moins un produit',
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        })
      }
    },
    async updateItemPrice(index) {
      if (this.form.order_items[index].glass) {
        const glassId = this.form.order_items[index].glass.id
        const glass = await APIRequest.detail('glasses', glassId)
        this.form.order_items[index].price = glass.price
        this.form.order_items[index].product_name = glass.name
        this.calculateTotal()
      }
    },
    calculateTotal() {
      // Calculate subtotal (without VAT)
      this.subtotalNoVAT = this.form.order_items.reduce(
        (sum, item) => sum + (parseFloat(item.price) || 0) * (parseInt(item.quantity) || 0),
        0
      );
      
      // Calculate VAT amount using global VAT rate
      const vatRate = parseFloat(this.form.vat_rate) || 0;

     
      
      this.subtotal = (this.subtotalNoVAT ) - ((this.subtotalNoVAT)/100)*this.promotionAmount;
      this.totalVAT = (this.subtotal + this.form.delivery_amount)/100 * vatRate;
      
      let total = this.subtotal + this.form.delivery_amount + this.totalVAT;
      
      
      
      this.total = total;
      this.form.amount = this.total;
    },
    async getUserAddresses() {
      this.addresses = []
      let addresses = await APIRequest.list('addresses', this.$route.params.id)
      this.addresses = addresses[0]

    },
    async loadUserDetails() {
      // Réinitialiser l'adresse
      this.form.address = {
        phone: '',
        address1: '',
        address2: '',
        city: '',
        postcode: '',
        country: ''
      };
      
      if (this.form.user) {
        try {
          const user = await Auth.get_user_with_id(this.form.user.id)
          // If user has addresses, use the first one
          await this.getUserAddresses()
          if (this.addresses && this.addresses.length > 0) {
            this.form.address = { ...this.addresses[0] }
          }
        } catch (error) {
          console.error('Error loading user details:', error)
        }
      }
    },
    async createOrder() {
      delete this.form.id
      this.$refs.simpleRules.validate().then(async success => {
        if (success) {
          this.loading = true
          try {
            const orderData = { ...this.form }
            
            if(!orderData.user && !orderData.custom_client_name) {
              this.loading = false
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Un client ou un nom client personnalisé est requis',
                  icon: 'AlertCircleIcon',
                  variant: 'warning',
                },
              })
              return
            }
            // Si un nom client personnalisé est défini mais pas d'utilisateur sélectionné
            if (!orderData.user && orderData.custom_client_name) {
              // Créer un objet client personnalisé pour l'API
              orderData.user = {
                name: orderData.custom_client_name
              }
              delete orderData.custom_client_name; // Supprimer la propriété qui ne sera pas utilisée par l'API
            }
            else if(this.form.user && this.form.user.value)
            {
              orderData.user={id:this.form.user.value}
            }
           
           
            // Format order items properly
            orderData.order_items = this.form.order_items.map(item => {
              const orderItem = {
                order_item_customizations: [],
                id:item.id,
                product_name:item.product_name,
                product_description:item.product_description,
                quantity: parseInt(item.quantity),
                price: parseFloat(item.price),
                amount: parseFloat(item.price) * parseInt(item.quantity)
              };

             if (item.glass) {
                // For regular glasses from the catalog
                orderItem.glass = { id: item.glass.value };
              }

              return orderItem;
            });

            for(let item of orderData.order_items) {
              
              if(item.id)
              {
              await APIRequest.update('order-items', item.id, item)
              }
              else
              {
                delete item.id
                item.order={id:this.$route.params.id}
                await APIRequest.create('order-items', item)
              }
            }

            //delete orderData.order_items

            // Add global VAT details
            orderData.vat_rate = parseFloat(this.form.vat_rate);
            orderData.status = this.form.status.value;
           

            orderData.invoice_status = this.form.invoice_status.value;
            orderData.payment_method = this.form.payment_method.value;

            orderData.client_type = this.form.client_type.value;
            
            // Ajouter la méthode de paiement
           // orderData.payment_method = this.form.payment_method;
            
            // Add promotion if applicable
            if (this.promotionAmount > 0) {
              orderData.promotion = {
                name: `Promo-${Math.random().toString(36).substr(2, 5)}`,
                code: `Promo-${Math.random().toString(36).substr(2, 5)}`,
                amount: this.promotionAmount,
                promotion_type: 'global'
              }
            }
            
            // Calculate final amount
            orderData.amount = this.total
            
            // Create order via API
            const createdOrder = await APIRequest.update('orders', this.$route.params.id, orderData)
            
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'La commande a été modifiée avec succès !',
                icon: 'CheckIcon',
                variant: 'success',
              },
            })
            
            this.$router.push({
              name: 'orderDetail', 
              params: { id: createdOrder.id }
            })
          } catch (error) {
            console.error('Error creating order:', error)
            this.loading = false
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Une erreur est survenue lors de la modification de la commande',
                icon: 'AlertCircleIcon',
                variant: 'danger',
              },
            })
          }
        }
      })
    },
    generatePDF() {
      // For jsPDF v2+
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(18);
      doc.text('Facture', 105, 20, { align: 'center' });
      
      // Add invoice info
      doc.setFontSize(12);
      doc.text(`Date: ${this.form.date}`, 20, 40);
      doc.text(`N° de commande: ${Math.floor(Math.random() * 10000)}`, 20, 50);
      doc.text(`Date d'échéance: ${this.form.due_date}`, 20, 60);
      
      // Add company info if available
      if (this.form.siret_number || this.form.vat_number) {
        let companyY = 70;
        if (this.form.siret_number) {
          doc.text(`SIRET: ${this.form.siret_number}`, 20, companyY);
          companyY += 10;
        }
        if (this.form.vat_number) {
          doc.text(`N° TVA: ${this.form.vat_number}`, 20, companyY);
        }
      }
      
      // Add client info
      doc.text('Client:', 140, 40);
      if (this.form.user) {
        const userName = this.userOptions.find(
          user => user.value === this.form.user.id
        )?.title || '';
        doc.text(userName, 140, 50);
      } else if (this.form.custom_client_name) {
        // Si c'est un client personnalisé
        doc.text(this.form.custom_client_name, 140, 50);
      }
      
      // Toujours afficher l'adresse
      if (this.form.address) {
        doc.text(this.form.address.address1 || '', 140, 60);
        doc.text(
          `${this.form.address.postcode || ''} ${this.form.address.city || ''}`,
          140,
          70
        );
        doc.text(this.form.address.country || '', 140, 80);
      }
      
      // Add items table with VAT
      const tableColumn = ['Produit', 'Quantité', 'Prix unitaire HT', 'Total HT', 'Total TTC'];
      const tableRows = [];
      
      this.form.order_items.forEach(item => {
        // Handle both custom products and catalog products
        let productName;
       
          productName = this.glassOptions.find(
            glass => glass.value === item.glass?.id
          )?.title || 'Produit sans nom';
        
        
        const unitPrice = parseFloat(item.price) || 0;
        const quantity = parseInt(item.quantity) || 0;
        const totalHT = unitPrice * quantity;
        const totalTTC = totalHT * (1 + this.form.vat_rate/100);
        
        tableRows.push([
          productName,
          quantity,
          `${unitPrice.toFixed(2)}€`,
          `${totalHT.toFixed(2)}€`,
          `${totalTTC.toFixed(2)}€`
        ]);
      });
      
      // Add products table
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 100,
        theme: 'striped',
        styles: { fontSize: 9 }, // Smaller font to fit all columns
        headStyles: { fillColor: [41, 128, 185] },
      });
      
      // Add totals after the table
      const finalY = doc.lastAutoTable.finalY + 10;
      
      doc.text('Montant HT:', 140, finalY);
      doc.text(`${this.subtotalNoVAT.toFixed(2)}€`, 175, finalY, { align: 'right' });
      
      doc.text(`TVA (${this.form.vat_rate}%)`, 140, finalY + 10);
      doc.text(`${this.totalVAT.toFixed(2)}€`, 175, finalY + 10, { align: 'right' });
      
      // Add VAT note if provided
      if (this.form.vat_note) {
        doc.text(this.form.vat_note, 100, finalY + 10, { align: 'center', maxWidth: 150 });
      }
      
      doc.text('Sous-total TTC:', 140, finalY + 20);
      doc.text(`${this.subtotal.toFixed(2)}€`, 175, finalY + 20, { align: 'right' });
      
      doc.text('Livraison:', 140, finalY + 30);
      doc.text(`${parseFloat(this.form.delivery_amount).toFixed(2)}€`, 175, finalY + 30, { align: 'right' });
      
      if (this.promotionAmount > 0) {
        doc.text('Promotion:', 140, finalY + 40);
        doc.text(`-${this.promotionAmount}%`, 175, finalY + 40, { align: 'right' });
        
        doc.text('TOTAL TTC:', 140, finalY + 50);
        doc.setFont(undefined, 'bold');
        doc.text(`${this.total.toFixed(2)}€`, 175, finalY + 50, { align: 'right' });
      } else {
        doc.text('TOTAL TTC:', 140, finalY + 40);
        doc.setFont(undefined, 'bold');
        doc.text(`${this.total.toFixed(2)}€`, 175, finalY + 40, { align: 'right' });
      }
      
      // Add notes
      if (this.form.note) {
        const notesStartY = this.promotionAmount > 0 ? finalY + 70 : finalY + 60;
        doc.setFont(undefined, 'normal');
        doc.text('Notes:', 20, notesStartY);
        doc.text(this.form.note, 20, notesStartY + 10);
      }
      
      // Save the PDF
      doc.save(`facture-${this.form.date}.pdf`);
    },
    async loadData() {
      try {
        // Load users
        const users = await Auth.list_users()

        this.userOptions = users.map(user => ({
          title: `${user.name} ${user.surname}`,
          value: user.id
        }))
        
        // Load glasses/products
        let glasses_list=await APIRequest.list('glasses', null, null, 100,1,null,this.searchTerm)
        

        const glasses = glasses_list[0]
        this.glassOptions = glasses.map(glass => ({
          title: glass.name,
          value: glass.id
        }))

       

        this.$forceUpdate()
        
        this.loading = false
      } catch (error) {
        console.error('Error loading data:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erreur lors du chargement des données',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
        this.loading = false
      }
    }
  },
 
  async mounted() {
  
    
    await this.loadData()
    this.order=await APIRequest.detail('orders',this.$route.params.id)
    this.form=this.order
    if(this.form.user.id!=null)
    {
      this.form.user=this.userOptions.find(user => user.value === this.order.user.id)
    }
   
    for(let item of this.form.order_items) {
      item.price=item.amount/item.quantity
      if(item.glass!=null)
      {
      
        item.glass=this.glassOptions.find(glass => glass.value === item.glass.id)
      }
    }
    this.form.status=this.statusOptions.find(status => status.value === this.order.status)
    this.form.invoice_status=this.invoiceStatusOptions.find(invoiceStatus => invoiceStatus.value === this.order.invoice_status)
    this.form.client_type=this.clientTypeOptions.find(clientType => clientType.value === this.order.client_type)
    this.form.payment_method=this.paymentMethodOptions.find(paymentMethod => paymentMethod.value === this.order.payment_method)
    
    // Convert DD/MM/YYYY to YYYY-MM-DD format for date inputs
    if (this.order.date) {
      const dateParts = this.order.date.split('/')
      if (dateParts.length === 3) {
        this.form.date = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`
      } else {
        this.form.date = this.order.date
      }
    } else {
      this.form.date = ''
    }
    
    if (this.order.due_date) {
      const dueDateParts = this.order.due_date.split('/')
      if (dueDateParts.length === 3) {
        this.form.due_date = `${dueDateParts[2]}-${dueDateParts[1]}-${dueDateParts[0]}`
      } else {
        this.form.due_date = this.order.due_date
      }
    } else {
      this.form.due_date = ''
    }

    if(this.order.promotion!=null)
    {
      this.promotionAmount=this.order.promotion.amount
    }
    
    this.calculateTotal()
   
    this.loading=false
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>